import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { AccordionCard } from '@modernloop/shared/components';
import { CircleWithCheckIcon, RadioIcon } from '@modernloop/shared/icons';
import { Box, Button, Stack, Typography } from '@mui/material';
import { last, sortBy } from 'lodash';

import { Debrief_DebriefRequirementsFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import DebriefContentEntity from 'src/entities/DebriefContent';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useHistory from 'src/hooks/useHistory';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import getDebriefFlowUrl from 'src/urls/getDebriefFlowUrl';

import CancelInterviewDialog from 'src/views-new/CandidateDetails/CancelInterviewDialog';
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import RequirementContainer from '../Common/RequirementContainer';

import RemoveDebriefRequirementConfirmation from './RemoveDebriefRequirementConfirmation';

type DebriefFragmentProps = {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  debriefRequirements: Debrief_DebriefRequirementsFragment;
};

type DebriefProps = {
  isCancelled?: boolean;
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const Debrief: FCWithFragments<DebriefFragmentProps, DebriefProps> = ({ debriefRequirements, isCancelled }) => {
  const { t } = useTranslation();
  const activityFeedRefetch = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const history = useHistory();

  const [showDeleteDebriefConfirmationModal, setShowDeleteDebriefConfirmationModal] = useState(false);
  const [showRemoveDebriefRequirementConfirmation, setShowRemoveDebriefRequirementConfirmation] = useState(false);

  const debriefFlowUrl = useMemo(() => {
    return getDebriefFlowUrl(debriefRequirements.application.id, { taskId: debriefRequirements.id });
  }, [debriefRequirements.application.id, debriefRequirements.id]);

  const handleScheduleDebrief = useCallback(() => {
    refetchScheduleTaskQuery();
    activityFeedRefetch();
    SidePanelManager.closeSidePanel();
    history.push(debriefFlowUrl);
  }, [activityFeedRefetch, debriefFlowUrl, history, refetchScheduleTaskQuery]);

  /**
   * This is a temporary work around to get the latest data.
   * BE is going to add a comparator and send the sorted array soon.
   */
  const taskDebrief = debriefRequirements?.debriefs
    ? last(
        sortBy(debriefRequirements.debriefs, (debrief) => {
          return new Date(debrief.createdAt);
        })
      )
    : null;

  const icon = useMemo((): React.ReactNode => {
    if (taskDebrief?.id) {
      return <CircleWithCheckIcon color="success" />;
    }
    return <RadioIcon />;
  }, [taskDebrief?.id]);

  const isDebriefCompleted = useMemo(() => {
    return new Date(taskDebrief?.endAt as string).getTime() < new Date().getTime();
  }, [taskDebrief?.endAt]);

  return (
    <RequirementContainer
      title={t('hiring-dashboard-tasks:tasks.debrief.scheduleDebrief')}
      icon={icon}
      moreMenuOptions={
        !isCancelled
          ? [{ id: 'Remove requirement', value: t('hiring-dashboard-tasks:tasks.debrief.removeRequirement') }]
          : undefined
      }
      onSelect={() => setShowRemoveDebriefRequirementConfirmation(true)}
    >
      <AccordionCard
        title={<Typography>{t('hiring-dashboard-tasks:tasks.debrief.debrief')}</Typography>}
        accordionDetailsPadding={0}
      >
        <Stack spacing={2}>
          {!taskDebrief?.id && (
            <Box py={1.5} px={2.5}>
              <Button
                data-testid="schedule-task-schedule-debrief-button"
                size="small"
                color="primary"
                onClick={handleScheduleDebrief}
              >
                {t('hiring-dashboard-tasks:tasks.debrief.scheduleDebrief')}
              </Button>
            </Box>
          )}

          {taskDebrief && <DebriefContentEntity debrief={taskDebrief} />}
        </Stack>
      </AccordionCard>

      <CancelInterviewDialog
        isCompleted={isDebriefCompleted}
        isDebrief
        interviewId={taskDebrief?.id}
        isOpen={showDeleteDebriefConfirmationModal}
        onCancel={() => {
          activityFeedRefetch();

          setShowDeleteDebriefConfirmationModal(false);
        }}
        onClose={() => setShowDeleteDebriefConfirmationModal(false)}
      />

      <RemoveDebriefRequirementConfirmation
        taskId={debriefRequirements.id}
        open={showRemoveDebriefRequirementConfirmation}
        onRemove={() => {
          activityFeedRefetch();
        }}
        onClose={() => {
          setShowRemoveDebriefRequirementConfirmation(false);
        }}
      />
    </RequirementContainer>
  );
};

Debrief.fragments = {
  debriefRequirements: gql`
    ${DebriefContentEntity.fragments.debrief}
    fragment Debrief_debriefRequirements on Task {
      id
      isDebriefRequired
      application {
        id
      }
      debriefs {
        id
        createdAt
        ...DebriefContentEntity_debrief
      }
    }
  `,
};

export default Debrief;
